import { Fragment, useCallback, useContext, useMemo, useRef } from 'react';
import type { FunctionComponent, MouseEvent } from 'react';

import MetaList from '../MetaList';
import SummaryItem from '../SummaryItem';
import type { ForwardProps } from '../../types';
import Flex from '../Flex';
import Icon, { registerIcon } from '../Icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import * as checkIcon from '../Icon/icons/check.icon';
import Text from '../Text';
import { Count } from '../Badges';
import BareButton from '../Button/BareButton';
import { withTestIds } from '../../utils';
import { useElement, useHighlight, useI18n, useDirection, useTestIds } from '../../hooks';
import Tooltip from '../Tooltip';
import Button from '../Button';

import menuHelpers from './helpers';
import type { AcceptedMouseEventElement, MenuItemProps } from './Menu.types';
import MenuContext from './Menu.context';
import { StyledAncestors, StyledCheckMarkIcon, StyledMenuItem } from './Menu.styles';
import { getMenuItemTestIds } from './Menu.test-ids';

registerIcon(caretLeftIcon, caretRightIcon, checkIcon);

const DrillDown = ({
  expandHandler
}: {
  expandHandler?: (e: MouseEvent<AcceptedMouseEventElement>) => void;
}) => {
  const { end } = useDirection();

  const icon = <Icon name={`caret-${end}`} />;

  return expandHandler ? (
    <Button as='span' icon variant='simple' aria-hidden onClick={expandHandler}>
      {icon}
    </Button>
  ) : (
    icon
  );
};

const AncestorPath = ({ ancestors = [] }: Pick<MenuItemProps, 'ancestors'>) => {
  const truncatedPath = ancestors.length > 2;
  const pathParts = truncatedPath ? [ancestors[0], ancestors[ancestors.length - 1]] : ancestors;

  const { end } = useDirection();

  return (
    <Flex
      container={{ gap: 0.5, alignItems: 'center' }}
      as={StyledAncestors}
      title={ancestors.map(a => (menuHelpers.isItem(a) ? a.primary : a.label)).join(' > ')}
    >
      {pathParts.map((ancestor, i, arr) => {
        const label = menuHelpers.isItem(ancestor) ? ancestor.primary : ancestor.label;
        return (
          <Fragment key={ancestor.id}>
            <Text variant='secondary'>{label}</Text>
            {i < arr.length - 1 && (
              <>
                <Text variant='secondary'>
                  <Icon name={`caret-${end}`} />
                </Text>
                {truncatedPath && (
                  <>
                    <Text variant='secondary'>…</Text>
                    <Text variant='secondary'>
                      <Icon name={`caret-${end}`} />
                    </Text>
                  </>
                )}
              </>
            )}
          </Fragment>
        );
      })}
    </Flex>
  );
};

const MenuItem: FunctionComponent<MenuItemProps & ForwardProps> = ({
  testId,
  id,
  primary,
  secondary,
  ancestors,
  visual: visualProp,
  count,
  items,
  selected,
  partial,
  href,
  tooltip,
  onClick,
  onExpand,
  disabled,
  role = 'menuitem',
  ...restProps
}: MenuItemProps & ForwardProps) => {
  const t = useI18n();
  const {
    mode,
    onItemClick,
    itemLayout,
    accent,
    variant: menuVariant,
    setFocusDescendant,
    getScopedItemId,
    arrowNavigationUnsupported
  } = useContext(MenuContext);

  const testIds = useTestIds(testId, getMenuItemTestIds);

  const selectionMode = mode === 'single-select' || mode === 'multi-select';
  const selectableParent = items && typeof selected === 'boolean';
  const itemId = useMemo(() => getScopedItemId(id), [id, getScopedItemId]);

  const itemCountId = `${itemId}-count`;
  const secondaryId = `${itemId}-secondary`;

  const clickHandler = useCallback(
    (e: MouseEvent<AcceptedMouseEventElement>) => {
      const mouseClick = e.detail > 0;

      if (mouseClick) {
        const target = e.target as HTMLElement;
        const focusAtEl =
          target.getAttribute('role') === role ? target : target.closest(`li[role="${role}"]`);

        if (focusAtEl) setFocusDescendant(focusAtEl as HTMLElement);
      }
      onClick?.(id, e);
      onItemClick?.(id, e);
    },
    [onClick, onItemClick, id]
  );

  const expandHandler = useCallback(
    (e: MouseEvent<AcceptedMouseEventElement>) => {
      onExpand?.(id, e);
      e.stopPropagation();
    },
    [onExpand, id]
  );

  const primaryRef = useRef<HTMLElement>(null);
  const accentPattern = useMemo(() => {
    if (!accent || items) return undefined;
    return typeof accent === 'function' ? accent(primary) : accent;
  }, [accent, items, primary]);
  useHighlight(primaryRef, accentPattern);

  const secondaryContent = ancestors ? (
    <AncestorPath ancestors={ancestors} />
  ) : (
    secondary && <MetaList items={secondary} id={secondaryId} />
  );

  const [itemRef, setItemRef] = useElement();

  const visual = (
    <>
      {selectionMode && (!items || selectableParent) && !disabled && (
        <StyledCheckMarkIcon name='check' />
      )}
      {visualProp}
    </>
  );
  const summaryItem = (
    <>
      <SummaryItem
        primary={!items ? <Text ref={primaryRef}>{primary}</Text> : primary}
        secondary={secondaryContent}
        layout={itemLayout}
        visual={visual}
        actions={
          items || count ? (
            <Flex container={{ gap: 1, alignItems: 'center', justify: 'center' }}>
              {count !== undefined && (
                <Count id={itemCountId} aria-label={t('menu_item_count', [count])}>
                  {count}
                </Count>
              )}
              {items && <DrillDown expandHandler={selectableParent ? expandHandler : undefined} />}
            </Flex>
          ) : undefined
        }
      />
      {tooltip && (
        <Tooltip target={itemRef} hideDelay='none' showDelay='short'>
          {tooltip}
        </Tooltip>
      )}
    </>
  );

  const itemLabel = useMemo(() => {
    const collapsedLabel = t('menu_collapsed', [primary]);
    if (items) {
      return arrowNavigationUnsupported
        ? t('menu_item_shift_space_expand_collapse', [collapsedLabel])
        : t('menu_item_expand_arrow', [collapsedLabel]);
    }
    return primary;
  }, [selected, primary, items]);

  const ariaDescribedBy = useMemo(() => {
    const hasSecondary = !!secondary;
    let idString: string;

    if (hasSecondary) {
      idString = secondaryId;
      if (count !== undefined && count >= 0) {
        idString = `${idString} ${itemCountId}`;
      }
    } else if (count !== undefined && count >= 0) {
      idString = itemCountId;
    } else idString = '';

    return idString;
  }, [items, secondary, itemCountId, secondaryId]);

  const isItemSelected = selectionMode && (!items || selectableParent) ? !!selected : undefined;

  return (
    <Flex
      ref={setItemRef}
      data-testid={testIds.root}
      {...restProps}
      container={{ alignItems: 'center', justify: 'between', gap: 1 }}
      id={itemId}
      as={StyledMenuItem}
      aria-label={itemLabel}
      aria-describedby={ariaDescribedBy}
      role={role}
      aria-disabled={disabled}
      data-expand={!!items}
      itemLayout={itemLayout}
      tabIndex={-1}
      onMouseDown={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault();
      }}
      onClick={(e: MouseEvent<AcceptedMouseEventElement>) => {
        if (disabled) {
          return;
        }
        return items && typeof selected !== 'boolean' ? expandHandler(e) : clickHandler(e);
      }}
      onMouseEnter={menuVariant === 'flyout' ? expandHandler : undefined}
      href={href}
      mode={mode}
      isParentItem={!!items}
      aria-selected={role !== 'menuitem' ? isItemSelected : undefined}
      isChecked={isItemSelected}
      selectableParent={selectableParent}
    >
      {href && !selectionMode ? (
        <BareButton href={href} tabIndex='-1'>
          {summaryItem}
        </BareButton>
      ) : (
        summaryItem
      )}
    </Flex>
  );
};

export default withTestIds(MenuItem, getMenuItemTestIds);
